import { Cancel, Info } from "@mui/icons-material";
import React, { useState } from "react";
import { addOrUpdateDocFromPath, docRef } from "../../globals/services";

/**
 * Migrate-Devs modal — re-homes one or more developer documents to a chosen
 * vendor by overwriting their `vendorRef` field on `devs/{devId}`.
 *
 * How it works:
 *   1. The admin types a vendor name; matches are fetched via
 *      `firebase.getDocumentsByArrayMembershipInCollection` against the
 *      `vendors` collection's `nameIndex`.
 *   2. The admin picks one vendor from the dropdown — that becomes the
 *      migration target.
 *   3. Clicking the action button enters a two-step confirm: first click
 *      flips the button into "Confirm — migrate N dev(s)?" state, second
 *      click runs the writes.
 *   4. On run, every selected dev is checked: if its current `vendorRef`
 *      already equals the target, it is skipped (idempotent). Remaining
 *      devs get an `addOrUpdateDocFromPath` write in parallel via
 *      Promise.all. The summary ("X migrated, Y skipped, Z failed") is
 *      shown before the modal auto-closes ~2s later.
 *
 * Only `vendorRef` is touched — no other fields on the dev document are
 * modified.
 *
 * @param {object} props
 * @param {Array<object>} props.selectedDevsData - Array of dev row records
 *   from the AllResources grid. Each entry must carry at least `id` (the
 *   `devs/{devId}` doc id). For backwards compatibility callers that pass
 *   a single record will still work — the array can have length 1.
 * @param {(open: boolean) => void} props.setMigrateDevs - Closer callback
 *   passed from the parent grid component.
 * @param {object} props.firebase - Firebase wrapper providing
 *   `getDocumentsByArrayMembershipInCollection`.
 * @returns {JSX.Element}
 *
 * @example
 *   <MigrateDevs
 *     firebase={firebase}
 *     selectedDevsData={[{ id: "dev1", vendorRef: {...} }, { id: "dev2" }]}
 *     setMigrateDevs={setMigrateDevs}
 *   />
 *
 * How to test:
 *   - Mock `firebase.getDocumentsByArrayMembershipInCollection` to return
 *     a stub vendor list; assert search input + selection flow renders.
 *   - Stub `addOrUpdateDocFromPath` (`../../globals/services`) and assert
 *     the loop writes one entry per dev not already pointing at the
 *     target, and zero writes for devs already on the target.
 *   - Manual end-to-end: select N devs in /all-resources, open modal,
 *     pick a vendor, confirm, verify `vendorRef` updated on each dev.
 */
const MigrateDevs = (props) => {
  const [vendorData, setVendorData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedVendor, setSelectedVendor] = useState({});
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [result, setResult] = useState(null);

  const { selectedDevsData = [], setMigrateDevs } = props;
  const devCount = selectedDevsData.length;
  const isPlural = devCount !== 1;
  const devLabel = isPlural ? "developers" : "developer";

  /**
   * Searches the vendors collection by name and refreshes the dropdown.
   *
   * How it works: persists the synthetic event (since the change handler
   * is async), pushes the lowercased query into the `nameIndex` array
   * membership search exposed by the firebase wrapper, and writes the
   * result snapshot list into local state for rendering.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - DOM change event from
   *   the vendor search input.
   * @returns {Promise<void>}
   *
   * How to test: spy on
   *   `firebase.getDocumentsByArrayMembershipInCollection` and assert it
   *   is invoked with the lowercased query, `["nameIndex"]`, and a limit
   *   of 5. Verify `vendorData` state is set to the resolved snapshots.
   */
  const fetchAndSetData = async (e) => {
    e.persist();
    setSearchText(e.target.value);
    const vendorFetchData =
      await props.firebase.getDocumentsByArrayMembershipInCollection(
        "vendors",
        e.target.value.toLowerCase(),
        ["nameIndex"],
        "companyName",
        false,
        true,
        false,
        5
      );
    setVendorData(vendorFetchData);
  };

  /**
   * Locks in the admin's chosen vendor as the migration target.
   *
   * @param {string} companyName - Display name of the vendor.
   * @param {string} companyLogo - URL to the vendor logo (may be undefined).
   * @param {string} vendorID - Firestore doc id of the vendor.
   * @returns {void}
   */
  const selectVendor = (companyName, companyLogo, vendorID) => {
    setSearchText("");
    setSelectedVendor({ companyName, companyLogo, vendorID });
    setConfirming(false);
    setError("");
  };

  /**
   * Clears the currently selected target vendor and resets the confirm
   * state so the admin can pick a different vendor before re-running.
   *
   * @returns {void}
   */
  const deselectVendor = () => {
    setSearchText("");
    setSelectedVendor({});
    setConfirming(false);
    setError("");
  };

  /**
   * Migrates every selected dev to the chosen vendor.
   *
   * How it works: builds the target `vendorRef` once, walks every entry
   * in `selectedDevsData`, and short-circuits any dev whose existing
   * `vendorRef.path` already equals the target (idempotent skip — same
   * guard pattern used by the Spryte-Deploy seed script). Remaining devs
   * each get an `addOrUpdateDocFromPath(devs/{id}, { vendorRef })` call
   * run in parallel via Promise.all. Per-dev success/failure counts are
   * captured into `result` state and surfaced as a summary before the
   * modal auto-closes after ~2s.
   *
   * @returns {Promise<void>}
   *
   * @throws Catches per-dev errors so a single failure does not abort
   *   the rest of the batch. Aggregated failure count is shown in the
   *   summary.
   *
   * How to test: stub `addOrUpdateDocFromPath` to resolve for some IDs
   *   and reject for others; pass `selectedDevsData` with a mix of
   *   already-on-target devs and to-migrate devs; assert the resulting
   *   `result` object reports `migrated`, `skipped`, and `failed`
   *   counts that match the input mix.
   */
  const migrateDevs = async () => {
    if (!selectedVendor.vendorID) {
      setError("Please, select a vendor");
      return;
    }
    if (devCount === 0) {
      setError("No developers selected");
      return;
    }

    setIsMigrating(true);
    setError("");

    const newVendorRef = docRef(`vendors/${selectedVendor.vendorID}`);
    const targetPath = `vendors/${selectedVendor.vendorID}`;

    let migrated = 0;
    let skipped = 0;
    let failed = 0;

    const writes = selectedDevsData.map(async (dev) => {
      const currentPath = dev?.vendorRef?.path ?? dev?.vendorData?.ref?.path;
      if (currentPath === targetPath) {
        skipped += 1;
        return;
      }
      try {
        await addOrUpdateDocFromPath(`devs/${dev.id}`, {
          vendorRef: newVendorRef,
        });
        migrated += 1;
      } catch (e) {
        failed += 1;
      }
    });

    await Promise.all(writes);

    setIsMigrating(false);
    setResult({ migrated, skipped, failed });

    // Auto-close after the admin has had a moment to read the summary.
    setTimeout(() => setMigrateDevs(false), 2200);
  };

  /**
   * Handles the action-button click. Implements the two-step confirm:
   * first click validates inputs and flips into the "confirm" state;
   * second click runs the migration.
   *
   * @returns {void}
   */
  const handleActionClick = () => {
    if (isMigrating || result) return;
    if (Object.keys(selectedVendor).length === 0) {
      setError("Please, select a vendor");
      return;
    }
    if (devCount === 0) {
      setError("No developers selected");
      return;
    }
    if (!confirming) {
      setError("");
      setConfirming(true);
      return;
    }
    migrateDevs();
  };

  const actionLabel = (() => {
    if (isMigrating) return "Migrating…";
    if (result) {
      const parts = [];
      if (result.migrated) parts.push(`${result.migrated} migrated`);
      if (result.skipped) parts.push(`${result.skipped} skipped`);
      if (result.failed) parts.push(`${result.failed} failed`);
      return parts.length ? parts.join(", ") : "Nothing to do";
    }
    if (confirming) {
      return `Confirm — migrate ${devCount} ${devLabel}?`;
    }
    return `Migrate ${devCount > 0 ? devCount + " " : ""}${
      isPlural ? "Devs" : "Dev"
    }`;
  })();

  return (
    <div className="marketSearch request_form" id="marketSearchComponent">
      <div className="info-ctr">
        <Info className="market-info" />
        <p>
          Search for a vendor, pick one from the dropdown, then click{" "}
          <strong>Migrate</strong> to re-home {devCount} {devLabel} to that
          vendor. Devs already pointing at the chosen vendor are skipped.
        </p>
      </div>
      <div className="marketSearch_close">
        <Cancel
          className="marketSearch_close-icon"
          onClick={() => setMigrateDevs(false)}
        />
      </div>
      <div className="selectedStackDiv">
        {selectedVendor?.companyName && (
          <div
            className="selectedStackItem"
            key={selectedVendor?.selectedVendorID}
          >
            <div onClick={deselectVendor} className="close">
              <h3>X</h3>
            </div>
            <p>
              <img
                src={selectedVendor?.companyLogo}
                alt={selectedVendor?.companyName}
              />{" "}
              {selectedVendor?.companyName}
            </p>
          </div>
        )}
      </div>
      <input
        name="companyName"
        value={searchText}
        onChange={(e) => fetchAndSetData(e)}
        disabled={isMigrating || !!result}
      />
      <div
        className="stackDiv"
        style={{ marginBottom: vendorData.length !== 0 ? "12.5px" : "0" }}
      >
        {vendorData.length !== 0 ? (
          <h3 className="hrTitle">VENDORS</h3>
        ) : (
          <div></div>
        )}
        {vendorData.length !== 0 ? <hr></hr> : <div></div>}
        {vendorData?.map((vendor) => {
          return (
            <div
              className="stackItem"
              key={vendor?.id}
              onClick={() => {
                selectVendor(
                  vendor.data()?.companyName,
                  vendor.data()?.logo?.url,
                  vendor?.id
                );
              }}
            >
              <img src={vendor.data()?.logo?.url} alt="Vendor" />
              <h3>{vendor.data()?.companyName}</h3>
            </div>
          );
        })}
      </div>
      <div
        className="btnSearch"
        onClick={handleActionClick}
        style={{
          opacity: isMigrating || result ? 0.7 : 1,
          cursor: isMigrating || result ? "default" : "pointer",
        }}
      >
        <h3>{actionLabel}</h3>
      </div>
      {error !== "" ? (
        <div>
          <p style={{ color: "#EB3570", fontWeight: 600 }}>{error}</p>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

export default MigrateDevs;
